/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/no-array-index-key */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { BoltIcon, PlusIcon } from "@heroicons/react/20/solid";
import { BuildingOffice2Icon, LightBulbIcon, UserCircleIcon } from "@heroicons/react/24/outline";
import {
  ActionIcon, Box, Divider, Flex, Button, Collapse, Accordion,
  LoadingOverlay,
  Paper, ScrollArea, Select, Stack, Text, TextInput, Tooltip,
} from "@mantine/core";
import { IconInfoSquareRounded, IconSelector, IconSettings, IconChevronDown } from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { useAllPlaybooks, useWorkspaceObjectSchema } from "../../../../../hooks/QueryHooks";
import MButton from "../../../../../shared/ui/buttons/MButton";
import CustomSelectInput from "../../../../../shared/ui/inputs/select/CustomSelectInput";
import useWorkspaceStore from "../../../../../store/workspace/workspaceStore";
import SignalFilterPopover from "../../signal-overview/components/SignalFilterPopover";
import SignalFilterContainer from "../../signal-overview/components/SignalFilterContainer";
import ConditionsMeet from "./ConditionsMeet";
import CreatePlaybook from "./CreatePlaybook";
import Destinations from "./Destinations";
import TooltipPlaybookPreview from "./TooltipPlaybookPreview";
import SignalPreviewTable from "./SignalPreviewTable";
import { buildEntityOptions, getEntityLabel } from "../../../../../shared/filter/utils/entityDisplayUtils";
import ModelSelector from "../../../../../components/agno/ModelSelector";

const basedOnChoices = [
  { value: "usage_based", label: "Usage - hits a usage threshold" },
];

type Props = {
  workspaceSchema: any;
  signal: any;
  setName: any;
  setEntityRelation: any;
  setTrigger: any;
  onTimeWindowChange: any;
  handlePredicateAdd: any;
  handlePredicateClear: any;
  handlePredicateRemove: any;
  handlePredicateChange: any;
  handleFilterAdd: any;
  clearFilter: any;
  createAction: any;
  updateAction: any;
  deleteAction: any;
  playbooks: any;
  error: any;
  onSignalSubmit: any;
  clearSignal: any;
  total: any;
  setSuppressionPeriod: any;
  setModelKey: any;
  setPlaybook: any;
  containerClass?: string;
  height?: string;
  setSelectedFields?: any;
  setSelectedMetrics?: any;
  isAutomation?: boolean;
  setSignal?: any;
};

const NewSignalBody = ({
  workspaceSchema,
  signal,
  setPlaybook,
  setName,
  setEntityRelation,
  setTrigger,
  onTimeWindowChange,
  handlePredicateAdd,
  handlePredicateClear,
  handlePredicateRemove,
  handlePredicateChange,
  handleFilterAdd,
  clearFilter,
  updateAction,
  deleteAction,
  playbooks,
  error,
  createAction,
  onSignalSubmit,
  clearSignal,
  total,
  setSuppressionPeriod,
  setModelKey,
  containerClass,
  height,
  setSelectedFields,
  setSelectedMetrics,
  isAutomation = false,
  setSignal,
}: Props) => {
  const workspace = useWorkspaceStore((state) => state.workspace);
  const { data: playBookData }: any = useAllPlaybooks({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id,
    select: (pb: any) => pb?.data,
  });

  const selectedPlaybook = useMemo(
    () => playBookData?.find((item: any) => item?.id === signal?.automation_actions?.playbook_id),
    [playBookData, signal?.automation_actions?.playbook_id],
  );

  const [configOpen, setConfigOpen] = useState(true);

  const entityOptions = useMemo(
    () => buildEntityOptions(workspaceSchema?.data || workspaceSchema),
    [workspaceSchema],
  );

  return (
    <Paper
      p="md"
      className={`relative ${containerClass || ""}`}
      w="full"
    >
      <LoadingOverlay loaderProps={{ size: "md" }} c="main.5" visible={workspaceSchema.isLoading} overlayProps={{ blur: 2 }} />
      <ScrollArea h={height || "calc(100vh - 135px)"} offsetScrollbars>
        <Stack gap="md">
          <Accordion
            variant="separated"
            defaultValue="when"
          >
            {/* Configuration Section */}
            <Accordion.Item value="configuration">
              <Accordion.Control>
                <Flex align="center" gap="xs">
                  <ActionIcon size="sm" variant="light" color="blue" radius="xs">
                    <IconSettings size={15} />
                  </ActionIcon>
                  <Stack gap={3}>
                    <Text size="sm" lh={1} fw={600} c="gray.8">Configuration</Text>
                    <Text size="xs" lh={1} c="gray.5">Basic settings for this {isAutomation ? "automation" : "signal"}</Text>
                  </Stack>
                </Flex>
              </Accordion.Control>
              <Accordion.Panel>
                <Paper>
                  <Stack gap="md">
                    <Flex gap="md" wrap="wrap">
                      <div className="w-full">
                        <Text size="xs" fw={500} c="gray.7" mb={6}>Signal Name</Text>
                        <TextInput
                          value={signal?.name}
                          w={300}
                          onChange={(event) => setName(event.currentTarget.value)}
                          error={error && !signal?.name && "Name is required"}
                          placeholder={isAutomation ? "e.g., Auto-assign high-value accounts" : "e.g., High engagement signal"}
                          size="xs"
                        />
                      </div>
                      <div style={{ width: 200 }}>
                        <Text size="xs" fw={500} c="gray.7" mb={4}>Scope</Text>
                        <Select
                          value={signal?.entity_relation}
                          size="xs"
                          data={entityOptions}
                          onChange={(value: any) => {
                            setEntityRelation(value);
                          }}
                        />
                      </div>
                      <div style={{ width: 240 }}>
                        <Text size="xs" fw={500} c="gray.7" mb={4}>Trigger Type</Text>
                        <Select
                          size="xs"
                          value={signal?.trigger}
                          data={basedOnChoices}
                          onChange={(value: any) => setTrigger(value)}
                        />
                      </div>
                      {!isAutomation && (
                        <div style={{ width: 200 }}>
                          <Text size="xs" fw={500} c="gray.7" mb={4}>Frequency</Text>
                          <Select
                            value={signal?.automation_actions?.suppression_period}
                            onChange={(value: any) => setSuppressionPeriod(value)}
                            size="xs"
                            data={[
                              { label: "All time", value: "all_time" },
                              { label: "Daily", value: "daily" },
                            ]}
                            placeholder="Select frequency"
                            error={error && !signal?.automation_actions?.suppression_period}
                          />
                        </div>
                      )}
                      <div style={{ width: 240 }}>
                        <Text size="xs" fw={500} c="gray.7" mb={4}>AI Model</Text>
                        <ModelSelector
                          surface="signals"
                          value={signal?.automation_actions?.model_key ?? undefined}
                          onChange={(key) => setModelKey(key)}
                          className="h-[30px] w-full justify-between rounded border border-gray-300 bg-white"
                        />
                      </div>
                    </Flex>
                  </Stack>
                </Paper>
              </Accordion.Panel>
            </Accordion.Item>

            {/* When Section */}
            <Accordion.Item value="when">
              <Accordion.Control>
                <Flex align="center" gap="xs">
                  <ActionIcon size="sm" variant="light" color="violet" radius="xs">
                    <BoltIcon className="w-4 h-4" />
                  </ActionIcon>
                  <Stack gap={3}>
                    <Text size="sm" fw={600} lh={1} c="gray.8">When</Text>
                    <Text size="xs" lh={1} c="gray.5">Define trigger conditions</Text>
                  </Stack>
                </Flex>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="sm">
                  {/* Conditions Display with Edit Button */}
                  {(() => {
                    const hasValidPredicates = signal?.filters?.predicates?.some((group: any) =>
                      group.predicates?.some((pred: any) => pred.field && pred.field !== "")
                    );

                    return (
                      <Flex justify="space-between" align="center" gap="sm">
                        {hasValidPredicates ? (
                          <Box className="bg-gray-50 rounded border border-gray-200 p-2 flex-1">
                            <Flex gap="xs" wrap="wrap">
                              {signal.filters.predicates.map((group: any, groupIdx: number) => (
                                <Flex key={groupIdx} gap="xs" align="center">
                                  {group.predicates?.map((pred: any, predIdx: number) => {
                                    if (!pred.field) return null;

                                    let displayValue = pred.value;
                                    if (typeof pred.value === 'object' && pred.value !== null) {
                                      if (pred.value.start !== undefined && pred.value.end !== undefined) {
                                        displayValue = `${pred.value.start} - ${pred.value.end}`;
                                      } else if (Array.isArray(pred.value)) {
                                        displayValue = pred.value.join(', ');
                                      } else {
                                        displayValue = JSON.stringify(pred.value);
                                      }
                                    }

                                    return (
                                      <Box key={predIdx}>
                                        <Text size="xs" className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-50 text-purple-700 rounded border border-purple-200">
                                          <span className="font-medium text-[11px]">{pred.field.split('.').pop()}</span>
                                          <span className="text-purple-500 text-[10px]">{pred.operator}</span>
                                          <span className="font-semibold text-[11px]">{displayValue}</span>
                                        </Text>
                                        {predIdx < group.predicates.length - 1 && pred.field && (
                                          <Text size="xs" c="gray.5" fw={600} className="inline mx-1.5 text-[10px]">AND</Text>
                                        )}
                                      </Box>
                                    );
                                  })}
                                  {groupIdx < signal.filters.predicates.length - 1 && (
                                    <Text size="xs" c="gray.5" fw={600} className="mx-1.5 text-[10px]">OR</Text>
                                  )}
                                </Flex>
                              ))}
                            </Flex>
                          </Box>
                        ) : (
                          <Box className="bg-gray-50 rounded border border-dashed border-gray-300 p-3 text-center flex-1">
                            <Text size="xs" c="gray.5">No conditions added yet. Click &ldquo;Add Condition&rdquo; or &ldquo;Edit&ldquo; to get started.</Text>
                          </Box>
                        )}

                        <SignalFilterPopover
                          filters={signal?.filters}
                          entityRelation={signal?.entity_relation}
                          onApply={(newFilters) => {
                            const currentMetrics = signal?.metrics || [];
                            const currentFields = signal?.selected_fields || [];
                            const newMetrics = [...currentMetrics];
                            const newFields = [...currentFields];

                            newFilters?.predicates?.forEach((group: any) => {
                              group?.predicates?.forEach((predicate: any) => {
                                if (predicate.field && predicate.field !== "") {
                                  const isMetric = predicate.entity_type !== "column" &&
                                    !predicate.field.startsWith("users.") &&
                                    !predicate.field.startsWith("accounts.");

                                  if (isMetric && !newMetrics.includes(predicate.field)) {
                                    newMetrics.push(predicate.field);
                                  } else if (
                                    predicate.field.startsWith(`${signal?.entity_relation}.`)
                                    && !newFields.includes(predicate.field)
                                  ) {
                                    newFields.push(predicate.field);
                                  }
                                }
                              });
                            });

                            setSignal({
                              ...signal,
                              filters: newFilters,
                              metrics: newMetrics,
                              selected_fields: newFields
                            });

                            if (newMetrics.length !== currentMetrics.length) {
                              setSelectedMetrics(newMetrics);
                            }
                            if (newFields.length !== currentFields.length) {
                              setSelectedFields(newFields);
                            }
                          }}
                          target={(
                            <Button
                              variant="light"
                              size="xs"
                              leftSection={<PlusIcon className="h-3 w-3" />}
                            >
                              {signal?.filters?.predicates?.length > 0
                                ? `Edit (${signal.filters.predicates.reduce((acc: number, group: any) => acc + (group.predicates?.length || 0), 0)})`
                                : "Add"}
                            </Button>
                          )}
                        />
                      </Flex>
                    );
                  })()}

                  {error && (!signal?.filters?.predicates?.find((fil: any) => fil?.predicates?.find((item: any) => item?.field?.includes("metric"))))
                    && (
                      <Text color="red.5" size="xs" fw={600}>
                        At least one metric condition is required
                      </Text>
                    )}

                  {/* Preview */}
                  <Box>
                    <Text size="xs" fw={500} c="gray.7" mb={4}>Preview matching {getEntityLabel(signal?.entity_relation || "accounts", workspaceSchema?.data || workspaceSchema, "plural").toLowerCase()}</Text>
                    <SignalPreviewTable />
                  </Box>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            {/* Then Section */}
            <Accordion.Item value="then">
              <Accordion.Control>
                <Flex align="center" gap="xs">
                  <ActionIcon size="sm" variant="light" color="green" radius="xs">
                    <LightBulbIcon className="w-4 h-4" />
                  </ActionIcon>
                  <Stack gap={3}>
                    <Text size="sm" fw={600} lh={1} c="gray.8">Then</Text>
                    <Text size="xs" lh={1} c="gray.5">Configure actions to perform</Text>
                  </Stack>
                </Flex>
              </Accordion.Control>
              <Accordion.Panel>
                <Paper withBorder radius="md" p="sm" className="bg-white">
                  <Stack gap="sm">
                    {!isAutomation && (
                      <Flex gap="xs" align="end" wrap="wrap">
                        <div style={{ width: 280 }}>
                          <Text size="xs" fw={500} c="gray.7" mb={4}>Playbook</Text>
                          <Select
                            classNames={{
                              input: "overflow-hidden text-ellipsis whitespace-nowrap",
                            }}
                            size="xs"
                            data={playbooks?.length ? playbooks : []}
                            rightSection={(
                              <Flex gap={2} className="bg-white">
                                <Tooltip
                                  classNames={{
                                    tooltip: "w-[450px]",
                                  }}
                                  label={playBookData ? <TooltipPlaybookPreview htmlContent={selectedPlaybook?.content} /> : "Select a playbook to see preview"}
                                >
                                  <IconInfoSquareRounded className="w-3 h-3 text-gray-500 hover:text-gray-700 cursor-context-menu" />
                                </Tooltip>
                                <IconSelector className="w-3 h-3 text-gray-500 cursor-pointer" />
                              </Flex>
                            )}
                            placeholder="None selected"
                            onChange={(value: any) => setPlaybook(value)}
                            value={signal?.automation_actions?.playbook_id}
                          />
                        </div>
                        <Text size="xs" c="gray.5" fw={500} className="mb-1">or</Text>
                        <CreatePlaybook>
                          <Button size="xs" variant="subtle" c="main.5">Create New</Button>
                        </CreatePlaybook>
                      </Flex>
                    )}

                    <Destinations
                      deleteAction={deleteAction}
                      setSelectedMetrics={setSelectedMetrics}
                      setSelectedFields={setSelectedFields}
                      signal={signal}
                      actions={signal?.automation_actions?.actions}
                      updateAction={updateAction}
                      playbook={selectedPlaybook}
                      isAutomation={isAutomation}
                    />
                  </Stack>
                </Paper>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </Stack>
      </ScrollArea>
    </Paper>
  );
};

export default NewSignalBody;
